<template>
  <div class="qr-scanner-container">
    <!-- Переключатель режимов -->
    <div class="mode-switcher" v-if="checkAdmin">
      <button @click="changeModule = !changeModule" class="mode-switch-btn">
        <span class="btn-text">{{
          changeModule ? "📦 Режим инвентаризации" : "👥 Режим контроля прохода"
        }}</span>
        <span class="btn-icon">🔄</span>
      </button>
    </div>

    <!-- Режим инвентаризации -->
    <div v-if="!changeModule" class="inventory-mode">
      <div class="mode-header">
        <div class="mode-title">
          <span class="mode-icon">📦</span>
          <h2>Режим инвентаризации</h2>
        </div>
      </div>

      <div class="inventory-layout">
        <!-- Левая часть: Сканер QR -->
        <div class="scanner-section">
          <div class="scanner-wrapper">
            <qrcode-stream
              :camera="camera"
              @decode="onDecode"
              @init="onInit"
              :track="paintOutline"
              class="qr-scanner"
            >
              <div v-show="showScanConfirmation" class="scan-confirmation">
                <div class="scan-success">
                  <svg class="success-icon" viewBox="0 0 1280 945">
                    <path
                      d="M9499 9382 c-31 -37 -181 -215 -334 -397 -1091 -1294 -2177 -2616
-3585 -4365 -746 -926 -1002 -1242 -1272 -1568 -442 -532 -664 -762 -738 -762
-31 0 -102 71 -195 195 -164 218 -370 557 -875 1440 -488 854 -655 1128 -752
1239 l-35 40 -92 -34 c-319 -115 -692 -349 -1061 -666 -187 -161 -560 -539
-560 -567 0 -40 577 -820 1199 -1622 761 -981 1385 -1736 1670 -2024 124 -125
276 -210 469 -263 105 -29 347 -31 472 -5 102 22 319 93 412 136 77 35 94 55
378 416 498 637 740 938 1100 1370 1848 2220 3966 4322 6242 6193 211 173 723
584 801 642 25 19 47 38 47 43 0 4 -15 7 -33 7 -55 0 -343 20 -517 36 -1030
93 -1937 289 -2670 578 -10 4 -33 -16 -71 -62z"
                    />
                  </svg>
                  <div v-if="text.length > 0" class="scanned-text">
                    {{ text }}
                  </div>
                </div>
              </div>
            </qrcode-stream>
          </div>

          <!-- Управление камерой -->
          <div class="camera-controls">
            <v-btn
              @click="switchCamera"
              class="control-btn"
              color="primary"
              outlined
            >
              <span class="btn-icon">📷</span>
              <span class="btn-label">Сменить камеру</span>
            </v-btn>

            <button
              v-if="camera === 'off'"
              @click="unpause"
              class="control-btn resume-btn"
            >
              <span class="btn-icon">▶️</span>
              <span class="btn-label">Продолжить сканирование</span>
            </button>

            <button
              v-if="camera !== 'off'"
              @click="pause"
              class="control-btn pause-btn"
            >
              <span class="btn-icon">⏸️</span>
              <span class="btn-label">Пауза</span>
            </button>
          </div>
        </div>

        <!-- Правая часть: Форма редактирования -->
        <div class="inventory-form-section">
          <div class="form-card">
            <div class="form-header">
              <h3>Инвентарная карточка</h3>
              <div class="item-id" v-if="changeINV.data._id">
                ID: {{ changeINV.data._id }}
              </div>
            </div>

            <div class="form-grid">
              <div class="form-group">
                <label class="form-label">Имя</label>
                <textarea
                  v-model="changeINV.data.name"
                  class="form-control"
                  rows="1"
                  placeholder="Введите наименование"
                ></textarea>
              </div>

              <div class="form-group">
                <label class="form-label">Школа</label>
                <select v-model="changeINV.data.school" class="form-select">
                  <option disabled :value="changeINV.data.school">
                    {{ changeINV.data.school || "Выбрать школу" }}
                  </option>
                  <option value="Старшая">Старшая</option>
                  <option value="Начальная">Начальная</option>
                  <option value="Иное">Иное</option>
                </select>
              </div>

              <div class="form-group">
                <label class="form-label">Местонахождение</label>
                <textarea
                  v-model="changeINV.data.location"
                  class="form-control"
                  rows="1"
                  placeholder="Введите местонахождение"
                ></textarea>
              </div>

              <div class="form-group">
                <label class="form-label">Инвентарный номер</label>
                <textarea
                  v-model="changeINV.data.schoolID"
                  class="form-control"
                  rows="1"
                  placeholder="Введите инвентарный номер"
                ></textarea>
              </div>

              <div class="form-group">
                <label class="form-label">Группа</label>
                <textarea
                  v-model="changeINV.data.group"
                  class="form-control"
                  rows="1"
                  placeholder="Введите группу"
                ></textarea>
              </div>

              <div class="form-group">
                <label class="form-label">Серийный номер</label>
                <textarea
                  v-model="changeINV.data.serialNumber"
                  class="form-control"
                  rows="1"
                  placeholder="Введите серийный номер"
                ></textarea>
              </div>

              <div class="form-group">
                <label class="form-label">Количество</label>
                <input
                  v-model="changeINV.data.quantity"
                  class="form-control"
                  type="number"
                  min="0"
                />
              </div>

              <div class="form-group">
                <label class="form-label">Федеральная программа</label>
                <textarea
                  v-model="changeINV.data.federalProgram"
                  class="form-control"
                  rows="1"
                  placeholder="Введите программу"
                ></textarea>
              </div>

              <div class="form-group">
                <label class="form-label">Статус</label>
                <select v-model="changeINV.data.status" class="form-select">
                  <option disabled :value="changeINV.data.status">
                    {{ changeINV.data.status || "Выбрать статус" }}
                  </option>
                  <option value="Используется">Используется</option>
                  <option value="Не используется">Не используется</option>
                  <option value="На списание">На списание</option>
                  <option value="Списан">Списан</option>
                </select>
              </div>

              <div class="form-group full-width">
                <label class="form-label">Комментарий</label>
                <textarea
                  v-model="changeINV.data.comment"
                  class="form-control"
                  rows="2"
                  placeholder="Введите комментарий"
                ></textarea>
              </div>
            </div>

            <!-- Кнопка обновления -->
            <div v-if="checkAdmin" class="form-actions">
              <v-btn @click="updateINV()" class="update-btn" color="success">
                <span class="btn-icon">💾</span>
                <span class="btn-label">Обновить данные</span>
              </v-btn>
            </div>

            <!-- QR код инвентарной карточки -->
            <div v-if="changeINV.data._id" class="qr-code-section">
              <div class="qr-code-wrapper">
                <VueQRCodeComponent
                  :text="changeINV.data._id"
                  :size="150"
                  color="#1a237e"
                  bg-color="#ffffff"
                  error-level="H"
                  class="inventory-qr"
                ></VueQRCodeComponent>
                <div class="qr-label">QR инвентарной карточки</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Режим контроля прохода -->
    <div v-if="changeModule" class="access-control-mode">
      <div class="mode-header">
        <div class="mode-title">
          <span class="mode-icon">👥</span>
          <h2>Режим контроля прохода</h2>
        </div>
      </div>

      <div class="access-control-layout">
        <!-- Левая часть: Сканер -->
        <div class="scanner-section">
          <div class="scanner-wrapper">
            <qrcode-stream
              :camera="camera"
              @decode="onDecode"
              @init="onInit"
              :track="paintOutline"
              class="qr-scanner"
            >
              <div v-show="showScanConfirmation" class="scan-confirmation">
                <div class="scan-success">
                  <svg class="success-icon" viewBox="0 0 1280 945">
                    <path
                      d="M9499 9382 c-31 -37 -181 -215 -334 -397 -1091 -1294 -2177 -2616
-3585 -4365 -746 -926 -1002 -1242 -1272 -1568 -442 -532 -664 -762 -738 -762
-31 0 -102 71 -195 195 -164 218 -370 557 -875 1440 -488 854 -655 1128 -752
1239 l-35 40 -92 -34 c-319 -115 -692 -349 -1061 -666 -187 -161 -560 -539
-560 -567 0 -40 577 -820 1199 -1622 761 -981 1385 -1736 1670 -2024 124 -125
276 -210 469 -263 105 -29 347 -31 472 -5 102 22 319 93 412 136 77 35 94 55
378 416 498 637 740 938 1100 1370 1848 2220 3966 4322 6242 6193 211 173 723
584 801 642 25 19 47 38 47 43 0 4 -15 7 -33 7 -55 0 -343 20 -517 36 -1030
93 -1937 289 -2670 578 -10 4 -33 -16 -71 -62z"
                    />
                  </svg>
                </div>
              </div>
            </qrcode-stream>
          </div>

          <div class="scanner-info">
            <!-- <div class="scanned-data">
              <span class="info-label">Считанный QR:</span>
              <span class="info-value">{{ text }}</span>
            </div> -->
            <button @click="switchCamera" class="camera-switch-btn">
              <span class="btn-icon">🔄</span>
              <span class="btn-label">Сменить камеру</span>
            </button>
            <button
              v-if="camera === 'off'"
              @click="unpause"
              class="control-btn resume-btn"
            >
              <span class="btn-icon">▶️</span>
              <span class="btn-label">Продолжить сканирование</span>
            </button>
            <button
              v-if="camera !== 'off'"
              @click="pause"
              class="control-btn pause-btn"
            >
              <span class="btn-icon">⏸️</span>
              <span class="btn-label">Пауза</span>
            </button>
            <div id="message_block_screen" style="color: black"></div>
          </div>
        </div>

        <!-- Правая часть: Информация о студенте -->
        <div class="student-info-section">
          <!-- Блок текущего студента -->
          <div class="current-student-card" :class="`status-${statusMark}`">
            <div v-if="student" class="student-details">
              <div class="student-name">
                {{ student.name }}
              </div>
              <div class="student-name" style="font-size: 12px">
                {{ getRole(student.role) }}
              </div>
              <div
                v-if="student.comment"
                class="student-name"
                style="font-size: 12px"
              >
                {{ student.comment }}
              </div>

              <div class="status-message">
                <div v-if="statusMark === 'OK'" class="status-ok">
                  <span class="status-icon">✅</span>
                  <span class="status-text">Родители оповещены</span>
                </div>
                <div v-if="statusMark === 'danger'" class="status-danger">
                  <span class="status-icon">⚠️</span>
                  <span class="status-text">Ошибка сканирования</span>
                </div>
                <div v-if="statusMark === 'empty'" class="status-empty">
                  <span class="status-icon">👤</span>
                  <span class="status-text">Ожидание сканирования</span>
                </div>
              </div>
            </div>
            <div v-if="student">
              <div v-if="student.photo">
                <img
                  :src="student.photo"
                  alt=""
                  width="150"
                  style="border-radius: 15px"
                />
              </div>
            </div>

            <div v-else class="no-student">
              <div class="scan-prompt">
                <span class="prompt-icon">📱</span>
                <span class="prompt-text">Отсканируйте QR-код ученика</span>
              </div>
            </div>
          </div>

          <!-- История сканирований -->
          <div class="scan-history">
            <div class="history-header">
              <h3>История проходов</h3>
              <span class="history-count">{{ printStudents.length }}</span>
            </div>

            <div class="history-list">
              <div
                v-for="(item, index) in printStudents.slice().reverse()"
                :key="index"
                class="history-item"
              >
                <span class="item-number">{{
                  printStudents.length - index
                }}</span>
                <span class="item-text">{{ item }}</span>
                <span class="item-time">{{
                  new Date().toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                }}</span>
              </div>

              <div v-if="printStudents.length === 0" class="empty-history">
                <span class="empty-icon">📝</span>
                <span class="empty-text">История проходов пуста</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { QrcodeStream } from "vue-qrcode-reader";
import TutorialDataService from "../services/TutorialDataService";
import VueQRCodeComponent from "vue-qrcode-component";
import SchoolPassService from "../services/SchoolPassService";
//import axios from "axios";
export default {
  components: {
    QrcodeStream,
    VueQRCodeComponent: VueQRCodeComponent,
  },
  props: ["checkAdmin", "classList"],
  data() {
    return {
      changeModule: this.checkAdmin ? true : false,
      camera: "off",
      wakeLock: null,
      cameraTemp: "off",
      result: "",
      error: "",
      changeINV: { data: {} },
      INV: [],
      printStudents: [],
      noRearCamera: false,
      noFrontCamera: false,
      text: "",
      id: null,
      arrOperation: [],
      student: false,
      clearTimerId: null,
      DateInternet: "",
      statusMark: "empty",
      qr: true,
      token: "",
      showScanConfirmation: false,
      studentsList: [],
    };
  },
  methods: {
    playSoundAccept() {
      // Путь относительно public директории или собранного dist
      const audioPath1 = require("@/assets/accept_sound.mp3");
      const audio1 = new Audio(audioPath1);
      audio1.volume = 1.0; // Громкость от 0 до 1
      audio1.play().catch((error) => {
        console.error("Ошибка воспроизведения:", error);
      });
      return audio1;
    },
    playSoundDanger() {
      // Путь относительно public директории или собранного dist
      const audioPath = require("@/assets/danger_sound.mp3");
      const audio = new Audio(audioPath);
      audio.volume = 1.0; // Громкость от 0 до 1
      audio.play().catch((error) => {
        console.error("Ошибка воспроизведения:", error);
      });
      return audio;
    },
    switchCamera() {
      switch (this.camera) {
        case "auto":
          this.camera = "rear";
          break;
        case "front":
          this.camera = "rear";
          break;
        case "rear":
          this.camera = "front";
          break;
      }
      console.log(this.camera);
    },
    paintOutline(detectedCodes, ctx) {
      for (const detectedCode of detectedCodes) {
        const [firstPoint, ...otherPoints] = detectedCode.cornerPoints;

        ctx.strokeStyle = "red";

        ctx.beginPath();
        ctx.moveTo(firstPoint.x, firstPoint.y);
        for (const { x, y } of otherPoints) {
          ctx.lineTo(x, y);
        }
        ctx.lineTo(firstPoint.x, firstPoint.y);
        ctx.closePath();
        ctx.stroke();
      }
    },
    async onInit(promise) {
      try {
        await promise;
      } catch (e) {
        console.error(e);
        this.camera = "auto";
      } finally {
        this.showScanConfirmation = this.camera === "off";
      }
    },
    showClassStudent(stud) {
      let className = "";
      console.log(this.classList);
      for (let i = 0; i <= this.classList.length; i++) {
        if (stud.classID == this.classList[i].classID) {
          className = this.classList[i].className;
          return className;
        }
        return className;
      }
    },

    async onDecode(content) {
      this.result = content;
      this.pause();

      // Сбрасываем предыдущий таймер, если он существует
      if (this.clearTimerId) {
        clearTimeout(this.clearTimerId);
        this.clearTimerId = null;
      }

      if (this.text != this.result) {
        this.statusMark = "empty";
        this.text = this.result;
        if (this.changeModule) {
          await this.findStudent(this.text);
          this.unpause();
          // Устанавливаем новый таймер с автоочисткой
          this.clearTimerId = setTimeout(() => {
            this.student = false;
            this.text = "";
            this.$forceUpdate();
            this.clearTimerId = null;
          }, 30000);
          // if (this.id) {
          //   clearTimeout(this.id);
          //   this.id = setTimeout(() => {
          //     if (this.text === this.result) {
          //       this.text = "";
          //       this.student = false;
          //     }
          //   }, 3000);

          //   await this.timeout(500);
          //   this.unpause();
          // }
        } else {
          this.beep2();
          await this.findINVByIDr(this.text);
        }
      } else {
        this.unpause();
      }
      //  if (this.id) clearTimeout(this.id);
      // this.id = setTimeout(() => {
      //   if (this.text === this.result) {
      //     this.text = "";
      //     this.student = false;
      //   }
      // }, 3000);

      // await this.timeout(500);
      // this.unpause();
    },

    unpause() {
      this.camera = this.cameraTemp;
    },

    pause() {
      this.cameraTemp = this.camera;
      this.camera = "off";
    },

    async timeout(ms) {
      return await new Promise((resolve) => {
        window.setTimeout(resolve, ms);
      });
    },
    // onDecode(a, b, c) {
    //   if (this.text != a) {
    //     this.statusMark = "empty";
    //     console.log(a, b, c);
    //     this.text = a;
    //     this.findStudent(this.text);
    //   }
    //   if (this.id) clearTimeout(this.id);
    //   this.id = setTimeout(() => {
    //     if (this.text === a) {
    //       this.text = "";
    //     }
    //   }, 3000);
    // },

    async findStudent(id) {
      console.log("НАЙТИ ПО ID ", id, this.studentsList, this.student);
      // for (let i = 0; i < this.studentsList.length; i++) {
      //   if (id === this.studentsList[i]._id) {
      //     this.beep2();
      //     this.student = this.studentsList[i];
      //     this.printStudents.push(
      //       this.studentsList[i].FirstName +
      //         " " +
      //         this.formattedDate(new Date()) +
      //         " (привязка для " +
      //         this.studentsList[i].telegram.length +
      //         " пользователей)"
      //     );
      //     console.log(this.printStudents);

      //     //telegram
      //     // for (let j = 0; j < this.studentsList[i].telegram.length; j++) {
      //     //   let msgRes =
      //     //     this.studentsList[i].FirstName +
      //     //     " " +
      //     //     this.studentsList[i].LastName +
      //     //     "\n" +
      //     //     "Проход на посту охраны" +
      //     //     "\n" +
      //     //     this.formattedDate(new Date());

      //     //   await axios
      //     //     .post(`https://api.telegram.org/bot${this.token}/sendMessage`, {
      //     //       chat_id: this.studentsList[i].telegram[j],
      //     //       text: msgRes,
      //     //     })
      //     //     .then(() => {
      //     //       this.statusMark = "OK";
      //     //       this.student = this.studentsList[i];
      //     //       this.beep2();
      //     //     })
      //     //     .catch((e) => {
      //     //       console.log(e.data, "ОШИБКА");
      //     //       this.student = this.studentsList[i];
      //     //       this.statusMark = "danger";
      //     //     });

      //     //   if (this.statusMark != "danger") {
      //     //     this.printStudents.push(
      //     //       this.studentsList[i].FirstName +
      //     //         " " +
      //     //         this.formattedDate(new Date()) +
      //     //         " (привязка для " +
      //     //         this.studentsList[i].telegram.length +
      //     //         " пользователей)"
      //     //     );
      //     //   }
      //     // }
      //   }
      //   //this.CreateMark(this.student);
      // }
      this.student = null;
      // await TutorialDataService.findStudentByID_securityCheck({ id: id })
      //   .then((response) => {
      //     this.student = response.data;
      //     console.log(this.student);
      //     //  this.beep2();
      //     if (this.student.LastName) this.playSoundAccept();
      //     else this.playSoundDanger();
      //     this.printStudents.push(
      //       this.showClassStudent(this.student) +
      //         " " +
      //         this.student.FirstName +
      //         " " +
      //         this.student.LastName +
      //         " " +
      //         this.formattedDate(new Date())
      //     );
      //     this.unpause();
      //   })
      //   .catch((e) => {
      //     console.log(e);
      //     this.playSoundDanger();
      //   });
      await SchoolPassService.getPassByID({ id: id })
        .then((response) => {
          this.student = response.data.data;
          console.log("student", response);
          //  this.beep2();
          if (this.student.name) this.playSoundAccept();
          else this.playSoundDanger();
          this.printStudents.push(
            //  this.showClassStudent(this.student.userID) +
            " " +
              this.student.name +
              " (" +
              this.formattedDate(new Date()) +
              ") - " +
              this.getRole(this.student.role),
          );
          this.unpause();
        })
        .catch((e) => {
          console.log(e);
          this.playSoundDanger();
        });
    },
    //let data = { id: id };
    // await TutorialDataService.findStudentByID(data)
    //   .then((response) => {
    //     this.student = response.data;

    //     for (let i = 0; i < this.student.telegram.length; i++) {
    //       let msgRes =
    //         this.student.FirstName +
    //         " " +
    //         this.student.LastName +
    //         "\n" +
    //         "Вход в школу" +
    //         "\n" +
    //         this.formattedDate(new Date());

    //       axios
    //         .post(`https://api.telegram.org/bot${this.token}/sendMessage`, {
    //           chat_id: this.student.telegram[i],
    //           text: msgRes,
    //         })
    //         .then(() => {
    //           this.statusMark = "OK";
    //           this.beep2();
    //         })
    //         .catch((e) => {
    //           console.log(e.data, "ОШИБКА");
    //           this.statusMark = "danger";
    //         });
    //     }
    //     //this.CreateMark(this.student);
    //   })
    //   .catch((e) => {
    //     console.log(e);
    //   });
    getRole(role) {
      if (role == "student") {
        return "Ученик";
      }
      if (role == "employee") {
        return "Сотрудник";
      }
      if (role == "teacher") {
        return "Учитель";
      }
      if (role == "parent") {
        return "Родитель";
      }
      if (role == "parent_comitet") {
        return "Родительский комитет";
      }
      if (role == "admin") {
        return "Администратор";
      }
      return "";
    },
    async findINVByIDr(id) {
      console.log(id);
      let data = { id: id };
      await TutorialDataService.findINVByID(data)
        .then((response) => {
          this.INV = response.data;
          console.log(this.INV);
          this.changeINV.data = response.data;
        })
        .catch((e) => {
          console.log(e);
        });
    },
    async updateINV() {
      let el = this.changeINV.data;
      console.log(this.changeINV.data);
      let data = {
        name: el.name,
        schoolID: el.schoolID,
        serialNumber: el.serialNumber,
        comment: el.comment,
        school: el.school,
        group: el.group,
        quantity: el.quantity,
        location: el.location,
        federalProgram: el.federalProgram,
        status: el.status,
      };

      await TutorialDataService.updateINV(el._id, data)
        .then((response) => {
          console.log("УСПЕШНО ОТПРАВЛЕНО", response);
          alert("Обновлено!");
        })
        .catch((e) => {
          console.log("1111111111", e);
        });
    },

    formattedDate: function (data) {
      let date = new Date(data);
      let temp;
      temp = date.toLocaleTimeString() + " " + date.toLocaleDateString();

      return temp;
    },
    beep2() {
      var snd = new Audio(
        "data:audio/wav;base64,//uQRAAAAWMSLwUIYAAsYkXgoQwAEaYLWfkWgAI0wWs/ItAAAGDgYtAgAyN+QWaAAihwMWm4G8QQRDiMcCBcH3Cc+CDv/7xA4Tvh9Rz/y8QADBwMWgQAZG/ILNAARQ4GLTcDeIIIhxGOBAuD7hOfBB3/94gcJ3w+o5/5eIAIAAAVwWgQAVQ2ORaIQwEMAJiDg95G4nQL7mQVWI6GwRcfsZAcsKkJvxgxEjzFUgfHoSQ9Qq7KNwqHwuB13MA4a1q/DmBrHgPcmjiGoh//EwC5nGPEmS4RcfkVKOhJf+WOgoxJclFz3kgn//dBA+ya1GhurNn8zb//9NNutNuhz31f////9vt///z+IdAEAAAK4LQIAKobHItEIYCGAExBwe8jcToF9zIKrEdDYIuP2MgOWFSE34wYiR5iqQPj0JIeoVdlG4VD4XA67mAcNa1fhzA1jwHuTRxDUQ//iYBczjHiTJcIuPyKlHQkv/LHQUYkuSi57yQT//uggfZNajQ3Vmz+Zt//+mm3Wm3Q576v////+32///5/EOgAAADVghQAAAAA//uQZAUAB1WI0PZugAAAAAoQwAAAEk3nRd2qAAAAACiDgAAAAAAABCqEEQRLCgwpBGMlJkIz8jKhGvj4k6jzRnqasNKIeoh5gI7BJaC1A1AoNBjJgbyApVS4IDlZgDU5WUAxEKDNmmALHzZp0Fkz1FMTmGFl1FMEyodIavcCAUHDWrKAIA4aa2oCgILEBupZgHvAhEBcZ6joQBxS76AgccrFlczBvKLC0QI2cBoCFvfTDAo7eoOQInqDPBtvrDEZBNYN5xwNwxQRfw8ZQ5wQVLvO8OYU+mHvFLlDh05Mdg7BT6YrRPpCBznMB2r//xKJjyyOh+cImr2/4doscwD6neZjuZR4AgAABYAAAABy1xcdQtxYBYYZdifkUDgzzXaXn98Z0oi9ILU5mBjFANmRwlVJ3/6jYDAmxaiDG3/6xjQQCCKkRb/6kg/wW+kSJ5//rLobkLSiKmqP/0ikJuDaSaSf/6JiLYLEYnW/+kXg1WRVJL/9EmQ1YZIsv/6Qzwy5qk7/+tEU0nkls3/zIUMPKNX/6yZLf+kFgAfgGyLFAUwY//uQZAUABcd5UiNPVXAAAApAAAAAE0VZQKw9ISAAACgAAAAAVQIygIElVrFkBS+Jhi+EAuu+lKAkYUEIsmEAEoMeDmCETMvfSHTGkF5RWH7kz/ESHWPAq/kcCRhqBtMdokPdM7vil7RG98A2sc7zO6ZvTdM7pmOUAZTnJW+NXxqmd41dqJ6mLTXxrPpnV8avaIf5SvL7pndPvPpndJR9Kuu8fePvuiuhorgWjp7Mf/PRjxcFCPDkW31srioCExivv9lcwKEaHsf/7ow2Fl1T/9RkXgEhYElAoCLFtMArxwivDJJ+bR1HTKJdlEoTELCIqgEwVGSQ+hIm0NbK8WXcTEI0UPoa2NbG4y2K00JEWbZavJXkYaqo9CRHS55FcZTjKEk3NKoCYUnSQ0rWxrZbFKbKIhOKPZe1cJKzZSaQrIyULHDZmV5K4xySsDRKWOruanGtjLJXFEmwaIbDLX0hIPBUQPVFVkQkDoUNfSoDgQGKPekoxeGzA4DUvnn4bxzcZrtJyipKfPNy5w+9lnXwgqsiyHNeSVpemw4bWb9psYeq//uQZBoABQt4yMVxYAIAAAkQoAAAHvYpL5m6AAgAACXDAAAAD59jblTirQe9upFsmZbpMudy7Lz1X1DYsxOOSWpfPqNX2WqktK0DMvuGwlbNj44TleLPQ+Gsfb+GOWOKJoIrWb3cIMeeON6lz2umTqMXV8Mj30yWPpjoSa9ujK8SyeJP5y5mOW1D6hvLepeveEAEDo0mgCRClOEgANv3B9a6fikgUSu/DmAMATrGx7nng5p5iimPNZsfQLYB2sDLIkzRKZOHGAaUyDcpFBSLG9MCQALgAIgQs2YunOszLSAyQYPVC2YdGGeHD2dTdJk1pAHGAWDjnkcLKFymS3RQZTInzySoBwMG0QueC3gMsCEYxUqlrcxK6k1LQQcsmyYeQPdC2YfuGPASCBkcVMQQqpVJshui1tkXQJQV0OXGAZMXSOEEBRirXbVRQW7ugq7IM7rPWSZyDlM3IuNEkxzCOJ0ny2ThNkyRai1b6ev//3dzNGzNb//4uAvHT5sURcZCFcuKLhOFs8mLAAEAt4UWAAIABAAAAAB4qbHo0tIjVkUU//uQZAwABfSFz3ZqQAAAAAngwAAAE1HjMp2qAAAAACZDgAAAD5UkTE1UgZEUExqYynN1qZvqIOREEFmBcJQkwdxiFtw0qEOkGYfRDifBui9MQg4QAHAqWtAWHoCxu1Yf4VfWLPIM2mHDFsbQEVGwyqQoQcwnfHeIkNt9YnkiaS1oizycqJrx4KOQjahZxWbcZgztj2c49nKmkId44S71j0c8eV9yDK6uPRzx5X18eDvjvQ6yKo9ZSS6l//8elePK/Lf//IInrOF/FvDoADYAGBMGb7FtErm5MXMlmPAJQVgWta7Zx2go+8xJ0UiCb8LHHdftWyLJE0QIAIsI+UbXu67dZMjmgDGCGl1H+vpF4NSDckSIkk7Vd+sxEhBQMRU8j/12UIRhzSaUdQ+rQU5kGeFxm+hb1oh6pWWmv3uvmReDl0UnvtapVaIzo1jZbf/pD6ElLqSX+rUmOQNpJFa/r+sa4e/pBlAABoAAAAA3CUgShLdGIxsY7AUABPRrgCABdDuQ5GC7DqPQCgbbJUAoRSUj+NIEig0YfyWUho1VBBBA//uQZB4ABZx5zfMakeAAAAmwAAAAF5F3P0w9GtAAACfAAAAAwLhMDmAYWMgVEG1U0FIGCBgXBXAtfMH10000EEEEEECUBYln03TTTdNBDZopopYvrTTdNa325mImNg3TTPV9q3pmY0xoO6bv3r00y+IDGid/9aaaZTGMuj9mpu9Mpio1dXrr5HERTZSmqU36A3CumzN/9Robv/Xx4v9ijkSRSNLQhAWumap82WRSBUqXStV/YcS+XVLnSS+WLDroqArFkMEsAS+eWmrUzrO0oEmE40RlMZ5+ODIkAyKAGUwZ3mVKmcamcJnMW26MRPgUw6j+LkhyHGVGYjSUUKNpuJUQoOIAyDvEyG8S5yfK6dhZc0Tx1KI/gviKL6qvvFs1+bWtaz58uUNnryq6kt5RzOCkPWlVqVX2a/EEBUdU1KrXLf40GoiiFXK///qpoiDXrOgqDR38JB0bw7SoL+ZB9o1RCkQjQ2CBYZKd/+VJxZRRZlqSkKiws0WFxUyCwsKiMy7hUVFhIaCrNQsKkTIsLivwKKigsj8XYlwt/WKi2N4d//uQRCSAAjURNIHpMZBGYiaQPSYyAAABLAAAAAAAACWAAAAApUF/Mg+0aohSIRobBAsMlO//Kk4soosy1JSFRYWaLC4qZBYWFRGZdwqKiwkNBVmoWFSJkWFxX4FFRQWR+LsS4W/rFRb/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////VEFHAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAU291bmRib3kuZGUAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAMjAwNGh0dHA6Ly93d3cuc291bmRib3kuZGUAAAAAAAAAACU=",
      );
      snd.play();
    },
    async CreateMark(data) {
      let datas = [];

      datas[0] = {
        date: this.DateInternet,
        classID: data.classID,
        studentID: data._id,
        causesID: "",
        cat: data.Category,
        countEating: data.countEating,
        qr: true,
      };
      await TutorialDataService.createMarks(datas)
        .then(async (response) => {
          console.log("Создано", response);
          if (response.data.countMarks == 1) {
            this.statusMark = "OK";
          } else {
            this.statusMark = "danger";
          }
        })
        .catch((e) => {
          console.log("1111111111", e);
        });
    },

    onLoaded() {
      console.log("load");
    },
    async getDate() {
      let date;
      await TutorialDataService.getDateWorld()
        .then((response) => {
          date = new Date(response.data).toISOString().slice(0, 10);
        })
        .catch((e) => {
          alert("ошибка получения даты");
          console.log(e);
        });

      return date;
    },
    async getTokenTelegramBot() {
      await TutorialDataService.getTelegramToken()
        .then((res) => {
          this.token = res.data;
        })
        .catch((e) => {
          console.log("1111111111", e);
        });
    },
    async getStudent() {
      await TutorialDataService.findStudentByClassID().then((response) => {
        let students = Object.values(response.data);
        students.map((student) => {
          if (student != undefined) {
            //  if (student.telegram.length > 0)
            this.studentsList.push(student);
          }
        });
        if (students.length > 0) {
          this.camera = "auto";
        }

        console.log(response, this.studentsList);
      });
    },
    async requestWakeLock() {
      try {
        this.wakeLock = await navigator.wakeLock.request("screen");
        document.getElementById("message_block_screen").textContent =
          "Сработала защита от блокировки экрана";
        console.log("Wake Lock is active");
        document.getElementById("message_block_screen").style.backgroundColor =
          "#dcffde";
      } catch (err) {
        console.error(`${err.name}, ${err.message}`);
        document.getElementById("message_block_screen").textContent =
          "Защита от блокировки экрана не сработала";
        document.getElementById("message_block_screen").style.backgroundColor =
          "#ffdcdc";
      }
    },
  },
  async mounted() {
    this.requestWakeLock();
    this.DateInternet = await this.getDate();
    //await this.getTokenTelegramBot();
    // await this.getStudent();
    this.camera = "auto";
  },
  destroyed() {
    this.camera = "off";
  },
};
</script>

<style scoped>
/* Основные стили */
.qr-scanner-container {
  padding: 20px;
  min-height: 100vh;
  background: linear-gradient(135deg, #1a237e 0%, #283593 100%);
  color: white;
}

/* Переключатель режимов */
.mode-switcher {
  margin-bottom: 30px;
  text-align: center;
}

.mode-switch-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.mode-switch-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: white;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
}

.btn-text {
  font-size: 14px;
}

.btn-icon {
  font-size: 18px;
}

/* Заголовок режима */
.mode-header {
  margin-bottom: 30px;
  text-align: center;
}

.mode-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 15px;
}

.mode-title h2 {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  background: linear-gradient(90deg, #4fc3f7, #e1f5fe);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.mode-icon {
  font-size: 32px;
}

.mode-stats {
  display: flex;
  justify-content: center;
  gap: 15px;
  flex-wrap: wrap;
}

.stat-badge {
  background: rgba(255, 255, 255, 0.1);
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* Общий лейаут */
.inventory-layout,
.access-control-layout {
  display: flex;
  gap: 30px;
  flex-wrap: wrap;
}

.scanner-section {
  flex: 1;
  min-width: 300px;
}

.inventory-form-section,
.student-info-section {
  flex: 1;
  min-width: 300px;
}

/* Сканер QR */
.scanner-wrapper {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 15px;
  margin-bottom: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

.qr-scanner {
  width: 100%;
  height: 300px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.scan-confirmation {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.scan-success {
  text-align: center;
  padding: 20px;
}

.success-icon {
  width: 80px;
  height: 80px;
  fill: #4caf50;
  margin-bottom: 15px;
}

.scanned-text {
  background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
  color: white;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  max-width: 280px;
  word-break: break-all;
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
}

/* Управление камерой */
.camera-controls {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
}

.control-btn {
  min-width: 180px;
  height: 48px;
  border-radius: 8px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.resume-btn {
  background: linear-gradient(135deg, #4caf50 0%, #2e7d32 100%);
  color: white;
  border: none;
  padding: 0 24px;
}

.pause-btn {
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
  color: white;
  border: none;
  padding: 0 24px;
}

.resume-btn:hover,
.pause-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
}

/* Форма инвентаризации */
.form-card {
  background: white;
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.form-header {
  margin-bottom: 25px;
  border-bottom: 2px solid #e8e8ff;
  padding-bottom: 15px;
}

.form-header h3 {
  color: #1a237e;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.item-id {
  color: #666;
  font-size: 14px;
  font-family: "Courier New", monospace;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 25px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-label {
  color: #1a237e;
  font-size: 14px;
  font-weight: 600;
}

.form-control,
.form-select {
  padding: 12px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: white;
}

.form-control:focus,
.form-select:focus {
  outline: none;
  border-color: #3f51b5;
  box-shadow: 0 0 0 3px rgba(63, 81, 181, 0.1);
}

textarea.form-control {
  min-height: 44px;
  resize: vertical;
}

.form-actions {
  display: flex;
  justify-content: center;
  margin: 30px 0;
}

.update-btn {
  height: 48px;
  padding: 0 32px;
  font-weight: 600;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

/* QR код инвентарной карточки */
.qr-code-section {
  text-align: center;
  padding-top: 25px;
  border-top: 2px solid #e8e8ff;
}

.qr-code-wrapper {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.inventory-qr {
  border: 8px solid white;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.qr-label {
  color: #666;
  font-size: 14px;
  font-weight: 500;
}

/* Режим контроля прохода - сканер */
.scanner-info {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 15px;
  margin-top: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.scanned-data {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
}

.info-value {
  font-size: 14px;
  font-weight: 500;
  color: white;
  word-break: break-all;
  max-width: 300px;
}

.camera-switch-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.camera-switch-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Карточка текущего студента */
.current-student-card {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 25px;
  margin-bottom: 25px;
  text-align: center;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.current-student-card.status-OK {
  background: linear-gradient(
    135deg,
    rgba(46, 125, 50, 0.9),
    rgba(76, 175, 80, 0.9)
  );
  border-color: #4caf50;
  animation: pulse 2s infinite;
}

.current-student-card.status-danger {
  background: linear-gradient(
    135deg,
    rgba(198, 40, 40, 0.9),
    rgba(244, 67, 54, 0.9)
  );
  border-color: #f44336;
}

.current-student-card.status-empty {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.2);
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
}

.date-display {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 15px;
  font-weight: 500;
}

.student-details {
  animation: slideUp 0.5s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.student-name {
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 15px;
  color: white;
}

.status-message {
  margin-top: 20px;
}

.status-ok,
.status-danger,
.status-empty,
.scan-prompt {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 500;
}

.status-ok {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.status-danger {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.status-empty {
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.7);
}

.no-student {
  padding: 20px;
}

.scan-prompt {
  background: rgba(255, 255, 255, 0.1);
  color: white;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 15px 25px;
  border-radius: 8px;
}

.prompt-icon {
  font-size: 24px;
}

.prompt-text {
  font-size: 16px;
  font-weight: 500;
}

/* История сканирований */
.scan-history {
  background: rgba(0, 0, 50, 0.7);
  border-radius: 16px;
  padding: 25px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.history-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: white;
}

.history-count {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
}

.history-list {
  max-height: 300px;
  overflow-y: auto;
  padding-right: 10px;
}

/* Кастомный скроллбар */
.history-list::-webkit-scrollbar {
  width: 6px;
}

.history-list::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

.history-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

.history-list::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

.history-item {
  background: rgba(255, 255, 255, 0.1);
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.3s ease;
  border-left: 4px solid #4caf50;
}

.history-item:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateX(5px);
}

.item-number {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.item-text {
  flex: 1;
  font-size: 14px;
  color: white;
  word-break: break-word;
}

.item-time {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  flex-shrink: 0;
}

.empty-history {
  text-align: center;
  padding: 40px 20px;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.empty-icon {
  font-size: 32px;
  opacity: 0.5;
}

.empty-text {
  font-size: 14px;
}

/* Адаптивность */
@media (max-width: 768px) {
  .qr-scanner-container {
    padding: 15px;
  }

  .inventory-layout,
  .access-control-layout {
    flex-direction: column;
    gap: 20px;
  }

  .scanner-section,
  .inventory-form-section,
  .student-info-section {
    width: 100%;
    min-width: unset;
  }

  .qr-scanner {
    height: 250px;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .camera-controls {
    flex-direction: column;
    align-items: center;
  }

  .control-btn {
    width: 100%;
    max-width: 300px;
  }

  .student-name {
    font-size: 24px;
  }

  .mode-title h2 {
    font-size: 20px;
  }
}

@media (max-width: 480px) {
  .mode-switch-btn {
    width: 100%;
    justify-content: center;
  }

  .scanner-info {
    flex-direction: column;
    align-items: stretch;
  }

  .camera-switch-btn {
    width: 100%;
    justify-content: center;
  }

  .info-value {
    max-width: 100%;
  }

  .history-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .item-time {
    align-self: flex-end;
  }
}
</style>
